import React from 'react';

import { IS_LOCAL, isEmpty } from '@nfctron/nexus-core';

import { useRouter } from 'next/router';

import { IS_DEPLOYED } from '@root/lib/constants/common';
import { type AppTrackingModuleRegisterConfigs } from '@root/lib/modules/next/next.module';
import { AnalyticsModule } from '@root/lib/modules/tracking/analytics.module';
import { FbPixelAdapter } from '@root/lib/modules/tracking/fb-pixel.adapter';
import { GoogleTagManagerAdapter } from '@root/lib/modules/tracking/google-tag-manager.adapter';
import { GtagJsAdapter } from '@root/lib/modules/tracking/gtag-js.adapter';
import { MsClarityAdapter } from '@root/lib/modules/tracking/ms-clarity.adapter';
import { SklikAdapter } from '@root/lib/modules/tracking/sklik.adapter';
import { TikTokPixelAdapter } from '@root/lib/modules/tracking/tiktok-pixel.adapter';
import { TrackingModule } from '@root/lib/modules/tracking/tracking.module';

const allowTracking = IS_DEPLOYED || IS_LOCAL;
const GA_TRACKING_ID = process.env.NEXT_PUBLIC_GA_TRACKING_ID;
const GTM_CONTAINER_ID = process.env.NEXT_PUBLIC_GTM_CONTAINER_ID;
const FB_PIXEL_TRACKING_ID = process.env.NEXT_PUBLIC_FB_PIXEL_TRACKING_ID;
const TTQ_PIXEL_TRACKING_ID = process.env.NEXT_PUBLIC_TTQ_PIXEL_TRACKING_ID;
const MS_CLARITY_TRACKING_ID = process.env.NEXT_PUBLIC_MS_CLARITY_ID;
const SKLIK_RETARGETING_ID = process.env.NEXT_PUBLIC_SKLIK_RETARGETING_ID;
export const useInitTrackingModule = (registerConfigs: AppTrackingModuleRegisterConfigs) => {
	const router = useRouter();

	/* -------------------------------------------------------------------------------------------------
	 * Tracking Module
	 * -----------------------------------------------------------------------------------------------*/
	const [trackingModule] = React.useState(() => {
		const fbPixelAdapter = new FbPixelAdapter(allowTracking);
		const gtagJsAdapter = new GtagJsAdapter(allowTracking);
		const googleTagManagerAdapter = new GoogleTagManagerAdapter(allowTracking);
		const tiktokPixelAdapter = new TikTokPixelAdapter(allowTracking);
		const sklikAdapter = new SklikAdapter(allowTracking);
		return new TrackingModule(fbPixelAdapter, gtagJsAdapter, googleTagManagerAdapter, tiktokPixelAdapter, sklikAdapter);
	});

	/** update GTM & Gtag.js tracking id upon receiving a new one */
	React.useEffect(() => {
		if (registerConfigs.googleTagManager.length > 0) registerConfigs.googleTagManager.forEach(trackingModule.googleTagManagerAdapter.register);
		if (registerConfigs.gtagJs.length > 0) registerConfigs.gtagJs.forEach(trackingModule.gtagJsAdapter.register);
		/** TODO: implement un-registering after the user redirects from event-scoped page */
	}, [registerConfigs.googleTagManager, registerConfigs.gtagJs, trackingModule.googleTagManagerAdapter.register, trackingModule.gtagJsAdapter]);

	/** update Facebook Pixel tracking id upon receiving a new one */
	React.useEffect(() => {
		if (registerConfigs.fbPixel.length > 0) registerConfigs.fbPixel.forEach(trackingModule.fbPixelAdapter.register);
		/** TODO: implement un-registering after the user redirects from event-scoped page */
	}, [registerConfigs.fbPixel, trackingModule.fbPixelAdapter]);

	/** update TikTok Pixel tracking id upon receiving a new one */
	React.useEffect(() => {
		if (registerConfigs.ttqPixel.length > 0) registerConfigs.ttqPixel.forEach(trackingModule.tiktokPixelAdapter.register);
		/** TODO: implement un-registering after the user redirects from event-scoped page */
	}, [registerConfigs.ttqPixel, trackingModule.tiktokPixelAdapter]);

	/** update Sklik tracking id upon receiving a new one */
	React.useEffect(() => {
		if (registerConfigs.sklik.length > 0) registerConfigs.sklik.forEach(trackingModule.sklikAdapter.register);
		/** TODO: implement un-registering after the user redirects from event-scoped page */
	}, [registerConfigs.sklik, trackingModule.sklikAdapter]);

	/** initially register internal static tracking id */
	React.useEffect(() => {
		if (!isEmpty(GA_TRACKING_ID)) trackingModule.gtagJsAdapter.register({ trackingId: GA_TRACKING_ID });
		if (!isEmpty(GTM_CONTAINER_ID)) trackingModule.googleTagManagerAdapter.register({ trackingId: GTM_CONTAINER_ID });
		if (!isEmpty(FB_PIXEL_TRACKING_ID)) trackingModule.fbPixelAdapter.register({ trackingId: FB_PIXEL_TRACKING_ID });
		if (!isEmpty(TTQ_PIXEL_TRACKING_ID)) trackingModule.tiktokPixelAdapter.register({ pixelId: TTQ_PIXEL_TRACKING_ID });
		if (!isEmpty(SKLIK_RETARGETING_ID)) trackingModule.sklikAdapter.register({ retargetingId: SKLIK_RETARGETING_ID });
	}, [
		trackingModule.fbPixelAdapter,
		trackingModule.googleTagManagerAdapter,
		trackingModule.gtagJsAdapter,
		trackingModule.tiktokPixelAdapter,
		trackingModule.sklikAdapter,
	]);

	/* -------------------------------------------------------------------------------------------------
	 * Analytics module
	 * -----------------------------------------------------------------------------------------------*/
	const [analyticsModule] = React.useState(() => {
		const msClarityAdapter = new MsClarityAdapter(allowTracking);
		return new AnalyticsModule(msClarityAdapter);
	});

	/** initially register internal static tracking id */
	React.useEffect(() => {
		if (!isEmpty(MS_CLARITY_TRACKING_ID)) analyticsModule.msClarityAdapter.register({ trackingId: MS_CLARITY_TRACKING_ID });
	}, [analyticsModule.msClarityAdapter, trackingModule.fbPixelAdapter, trackingModule.gtagJsAdapter, trackingModule.tiktokPixelAdapter]);

	/** update MsClarity tracking id upon receiving a new one */
	React.useEffect(() => {
		if (registerConfigs.msClarity.length > 0) registerConfigs.msClarity.forEach((id) => analyticsModule.msClarityAdapter.register(id));
	}, [registerConfigs.msClarity, analyticsModule.msClarityAdapter]);

	/** handle route changes for tracking purposes */
	React.useEffect(() => {
		/** initial pageView triggered only the first time */
		trackingModule.fbPixelAdapter.handlePageView();
		trackingModule.tiktokPixelAdapter.handlePageView();
		trackingModule.gtagJsAdapter.handlePageView(window.location.href);
		trackingModule.sklikAdapter.handlePageView({ rtgUrl: window.location.href });
		/** register route change events */
		router.events.on('routeChangeComplete', trackingModule.handlePageViewChange);
		return () => {
			router.events.off('routeChangeComplete', trackingModule.handlePageViewChange);
		};
	}, [
		trackingModule.fbPixelAdapter,
		trackingModule.handlePageViewChange,
		router.events,
		trackingModule.tiktokPixelAdapter,
		trackingModule.gtagJsAdapter,
		trackingModule.sklikAdapter,
	]);

	return { trackingModule, analyticsModule };
};
